import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, Phone, MessageCircle, Mail, MapPin, Calendar, Users,
  UserPlus, Plus, ArrowRight, X, Loader2, User, ExternalLink, Share2,
} from 'lucide-react';
import { cachedGet, invalidate } from '../lib/cache';
import { Avatar } from '../components/Avatar';
import { TripCard } from '../components/TripCard';
import { waNumber } from '../lib/ui';
import FollowUpManager from '@/components/followup/FollowUpManager';
import SendWhatsAppDialog from '@/components/SendWhatsAppDialog';
import { buildLeadWaContext, leadSuggestHint } from '@/utils/waContext';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SOURCE_LABEL = { 'walk-in': 'Walk-in', phone: 'Phone call', whatsapp: 'WhatsApp', referral: 'Referral', website: 'Website', manual: 'Manual', other: 'Other' };
const arr = (d) => (Array.isArray(d) ? d : (d?.items || d?.leads || []));
const fmtDate = (s) => { if (!s) return ''; const d = new Date(s); return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };

export const MobileLeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [lead, setLead] = useState(location.state?.lead || null);
  const [trips, setTrips] = useState([]);
  const [convertOpen, setConvertOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [match, setMatch] = useState(undefined); // undefined=loading | null=none | object=found
  const [converting, setConverting] = useState(false);
  const [creatingTrip, setCreatingTrip] = useState(false);
  const [sharingQuote, setSharingQuote] = useState(false);

  useEffect(() => {
    if (lead) return;
    cachedGet('/api/website-leads?limit=500').then((d) => {
      const found = arr(d).find((l) => l.leadId === leadId);
      if (found) setLead(found); else { toast.error('Lead not found'); navigate(-1); }
    }).catch(() => { toast.error('Could not load lead'); navigate(-1); });
  }, [leadId, lead, navigate]);

  const loadTrips = () => cachedGet(`/api/trips/by-lead/${leadId}`, { force: true }).then((d) => setTrips(arr(d))).catch(() => {});
  useEffect(() => {
    loadTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const wa = useMemo(() => (lead ? waNumber(lead.countryCode, lead.whatsapp || lead.phone) : ''), [lead]);
  const converted = !!lead?.clientUCC && (lead?.status || '').toLowerCase() === 'converted';

  if (!lead) {
    return <div className="fixed inset-0 bg-white flex items-center justify-center" data-testid="m-lead-loading"><Loader2 className="h-6 w-6 animate-spin text-[#0F6E56]" /></div>;
  }

  const openConvert = async () => {
    setConvertOpen(true); setMatch(undefined);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/website-leads/${leadId}/traveller-match`, { withCredentials: true });
      setMatch(r.data?.match || null);
    } catch { setMatch(null); }
  };

  const doConvert = async (ucc) => {
    setConverting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/website-leads/${leadId}/convert-traveller`, ucc ? { ucc } : {}, { withCredentials: true });
      invalidate('/api/website-leads'); invalidate('/api/clients');
      toast.success(r.data?.created ? 'Traveller profile created' : 'Linked to traveller');
      navigate(`/m/clients/${r.data.ucc}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Conversion failed');
    } finally { setConverting(false); }
  };

  const createTrip = async () => {
    setCreatingTrip(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/trips/from-lead/${leadId}`, {}, { withCredentials: true });
      invalidate('/api/trips');
      toast.success(`Trip ${r.data?.displayTripId || ''} created`);
      setTrips((prev) => [r.data, ...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create trip');
    } finally { setCreatingTrip(false); }
  };

  const convertEnquiry = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/website-leads/${leadId}/status`, { status: 'qualified', notes: 'Converted to enquiry' }, { withCredentials: true });
      invalidate('/api/website-leads');
      toast.success('Converted to Enquiry');
      setLead((l) => ({ ...l, status: 'qualified' }));
    } catch { toast.error('Failed to convert'); }
  };

  // Share the client-facing quote/itinerary link for this lead's trip.
  // Uses the lead's first linked trip → its itinerary public preview URL.
  const shareQuote = async () => {
    if (!trips.length) { toast.error('Create a trip first, then build an itinerary to share.'); return; }
    setSharingQuote(true);
    try {
      const trip = trips[0];
      const r = await axios.get(`${BACKEND_URL}/api/itineraries?tripId=${trip.tripId}`, { withCredentials: true });
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      const itin = list[0];
      if (!itin) { toast.error('Build an itinerary for this trip first.'); return; }
      const ref = itin.public_ref || itin.id;
      let base = window.location.origin;
      try { if (itin.public_url) base = new URL(itin.public_url).origin; } catch (_) { /* noop */ }
      const url = itin.public_url || `${base}/${ref}/preview`;
      const phone = String(lead.whatsapp || lead.phone || '').replace(/[^0-9]/g, '');
      const cc = String(lead.countryCode || '').replace(/[^0-9]/g, '');
      const text = encodeURIComponent(`Hi ${lead.name || ''}, here is your ${lead.destination || trip.destination || 'trip'} quote: ${url}`);
      window.open(phone ? `https://wa.me/${cc}${phone}?text=${text}` : `https://wa.me/?text=${text}`, '_blank', 'noopener');
      try { await navigator.clipboard.writeText(url); } catch (_) { /* noop */ }
    } catch (e) {
      toast.error('Could not load the quote link');
    } finally { setSharingQuote(false); }
  };

  const tel = () => { if (lead.phone) window.location.href = `tel:${lead.countryCode || ''}${lead.phone}`; };
  const waOpenSheet = () => { if (lead.phone) setWaOpen(true); else toast.error('No phone on file for this lead'); };

  return (
    <div className="fixed inset-0 bg-[#F8F9FA] flex flex-col" data-testid="m-lead-detail">
      <header className="h-[52px] bg-white border-b border-[#E5E7EB] flex items-center gap-2 px-2 shrink-0">
        <button type="button" onClick={() => navigate(-1)} data-testid="m-lead-back" aria-label="Back" className="w-11 h-11 flex items-center justify-center text-[#111827]"><ArrowLeft className="h-5 w-5" /></button>
        <p className="text-[15px] font-semibold text-[#111827] truncate flex-1">{lead.name || 'Lead'}</p>
        <span className="shrink-0 mr-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#E7F1ED] text-[#0F6E56] capitalize">{lead.status || 'new'}</span>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ paddingBottom: 'calc(24px + env(safe-area-inset-bottom))' }}>
        {/* Lead info */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="m-lead-card">
          <div className="flex items-center gap-3">
            <Avatar name={lead.name} size={48} />
            <div className="min-w-0 flex-1">
              <p className="text-[17px] font-semibold text-[#111827] leading-tight truncate">{lead.name}</p>
              <p className="text-[12px] text-[#6B7280] mt-0.5">{SOURCE_LABEL[lead.source] || lead.source || 'Lead'}{lead.leadId ? ` · ${lead.leadId}` : ''}</p>
            </div>
          </div>
          {lead.destination && <p className="mt-3 text-[13px] text-[#111827] flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#6B7280]" />{lead.destination}</p>}
          {(lead.travelDate || lead.travelEndDate) && <p className="mt-1.5 text-[13px] text-[#6B7280] flex items-center gap-1.5"><Calendar className="h-4 w-4" />{[fmtDate(lead.travelDate), fmtDate(lead.travelEndDate)].filter(Boolean).join(' – ')}</p>}
          {lead.travellersCount ? <p className="mt-1.5 text-[13px] text-[#6B7280] flex items-center gap-1.5"><Users className="h-4 w-4" />{lead.travellersCount} traveller{lead.travellersCount === 1 ? '' : 's'}</p> : null}
          {lead.phone && <a href={`tel:${lead.countryCode || ''}${lead.phone}`} className="mt-1.5 text-[13px] text-[#0F6E56] flex items-center gap-1.5"><Phone className="h-4 w-4" />{lead.countryCode || ''} {lead.phone}</a>}
          {lead.email && <a href={`mailto:${lead.email}`} className="mt-1.5 text-[13px] text-[#0F6E56] flex items-center gap-1.5 break-words"><Mail className="h-4 w-4 shrink-0" /><span className="break-words">{lead.email}</span></a>}
          {lead.message && <p className="mt-2.5 text-[13px] text-[#6B7280] bg-[#F8F9FA] rounded-lg p-2.5">{lead.message}</p>}

          {/* Quick contact */}
          {lead.phone && (
            <div className="grid grid-cols-2 gap-2.5 mt-3">
              <button type="button" onClick={tel} data-testid="m-lead-call" className="h-11 rounded-lg border border-[#E5E7EB] text-[#111827] text-[14px] font-medium flex items-center justify-center gap-1.5"><Phone className="h-4 w-4" /> Call</button>
              <button type="button" onClick={waOpenSheet} data-testid="m-lead-wa" className="h-11 rounded-lg border border-[#E5E7EB] text-[#0F6E56] text-[14px] font-medium flex items-center justify-center gap-1.5"><MessageCircle className="h-4 w-4" /> WhatsApp</button>
            </div>
          )}
        </div>

        {/* Convert / create actions */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 space-y-2.5" data-testid="m-lead-actions">
          {converted ? (
            <button type="button" onClick={() => navigate(`/m/clients/${lead.clientUCC}`)} data-testid="m-lead-view-traveller"
              className="w-full h-12 rounded-lg border border-[#0F6E56] text-[#0F6E56] text-[14px] font-medium flex items-center justify-center gap-2"><User className="h-4 w-4" /> View traveller profile <ExternalLink className="h-4 w-4" /></button>
          ) : (
            <button type="button" onClick={openConvert} data-testid="m-lead-convert-traveller-btn"
              className="w-full h-12 rounded-lg text-white text-[14px] font-medium flex items-center justify-center gap-2" style={{ backgroundColor: '#0F6E56' }}><UserPlus className="h-4 w-4" /> Convert to Traveller</button>
          )}
          <button type="button" onClick={createTrip} disabled={creatingTrip} data-testid="m-lead-create-trip-btn"
            className="w-full h-12 rounded-lg border border-[#E5E7EB] text-[#111827] text-[14px] font-medium flex items-center justify-center gap-2">
            {creatingTrip ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create Trip
          </button>
          <button type="button" onClick={shareQuote} disabled={sharingQuote} data-testid="m-lead-share-quote-btn"
            className="w-full h-12 rounded-lg border border-[#E5E7EB] text-[#0F6E56] text-[14px] font-medium flex items-center justify-center gap-2">
            {sharingQuote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share Quote
          </button>
          {['new', 'contacted'].includes((lead.status || 'new').toLowerCase()) && (
            <button type="button" onClick={convertEnquiry} data-testid="m-lead-convert-enquiry-btn"
              className="w-full h-11 rounded-lg text-[#6B7280] text-[13px] font-medium flex items-center justify-center gap-1.5">Mark as Enquiry <ArrowRight className="h-4 w-4" /></button>
          )}
        </div>

        {/* Linked trips */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="m-lead-linked-trips">
          <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7280] mb-2.5">Linked trips{trips.length ? ` (${trips.length})` : ''}</h2>
          {trips.length > 0 ? (
            <div className="space-y-2.5">{trips.map((tr) => <TripCard key={tr.id} trip={tr} />)}</div>
          ) : (
            <p className="text-[12px] text-[#6B7280]">No trips linked yet. Tap <span className="font-medium">Create Trip</span> to start one for this lead.</p>
          )}
        </div>

        {/* Follow-ups (manual + auto-suggested) */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="m-lead-followups">
          <FollowUpManager leadId={leadId} />
        </div>
      </div>

      <SendWhatsAppDialog
        open={waOpen}
        onOpenChange={setWaOpen}
        title={lead.name}
        phone={wa}
        context={buildLeadWaContext(lead, { user })}
        suggestHint={leadSuggestHint(lead)}
      />

      {/* Convert-to-traveller bottom sheet */}
      {convertOpen && (
        <>
          <button type="button" aria-label="Close" onClick={() => setConvertOpen(false)} className="fixed inset-0 z-[55] bg-black/30" />
          <div className="fixed inset-x-0 bottom-0 z-[56] bg-white rounded-t-2xl border-t border-[#E5E7EB] p-4" style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }} data-testid="m-lead-convert-sheet">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[15px] font-semibold text-[#111827]">Convert to Traveller</p>
              <button type="button" onClick={() => setConvertOpen(false)} aria-label="Close" className="w-9 h-9 flex items-center justify-center text-[#6B7280]"><X className="h-5 w-5" /></button>
            </div>

            {match === undefined ? (
              <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#0F6E56]" /></div>
            ) : match ? (
              <div data-testid="m-lead-match-existing">
                <p className="text-[13px] text-[#6B7280] mb-2">A traveller with this phone already exists:</p>
                <div className="flex items-center gap-3 border border-[#E5E7EB] rounded-xl p-3 mb-3">
                  <Avatar name={match.fullName} size={40} />
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold text-[#111827] truncate">{match.fullName || 'Traveller'}</p>
                    <p className="text-[12px] text-[#6B7280]">{match.displayUCC} · {match.phone}</p>
                  </div>
                </div>
                <button type="button" onClick={() => doConvert(match.ucc)} disabled={converting} data-testid="m-lead-link-existing-btn"
                  className="w-full h-12 rounded-lg text-white text-[14px] font-medium flex items-center justify-center gap-2" style={{ backgroundColor: '#0F6E56' }}>
                  {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Link to existing traveller <ArrowRight className="h-4 w-4" /></>}
                </button>
              </div>
            ) : (
              <div data-testid="m-lead-create-traveller">
                <p className="text-[13px] text-[#6B7280] mb-3">Create a traveller profile for <span className="font-medium text-[#111827]">{lead.name}</span>?</p>
                <div className="space-y-1.5 border border-[#E5E7EB] rounded-xl p-3 mb-3">
                  <PrefillRow label="Name" value={lead.name} />
                  <PrefillRow label="Phone" value={`${lead.countryCode || ''} ${lead.phone || ''}`.trim() || '—'} />
                  <PrefillRow label="WhatsApp" value={lead.whatsapp || lead.phone || '—'} />
                  <PrefillRow label="Email" value={lead.email || '—'} />
                </div>
                <button type="button" onClick={() => doConvert(null)} disabled={converting} data-testid="m-lead-create-traveller-btn"
                  className="w-full h-12 rounded-lg text-white text-[14px] font-medium flex items-center justify-center gap-2" style={{ backgroundColor: '#0F6E56' }}>
                  {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4" /> Create &amp; convert</>}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const PrefillRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-[12px] text-[#6B7280] shrink-0">{label}</span>
    <span className="text-[13px] font-medium text-[#111827] text-right break-words">{value}</span>
  </div>
);

export default MobileLeadDetail;
